import { useCallback, useMemo, useState } from 'react'

const compareText = (a, b) =>
	String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })

const compareNumber = (a, b) => Number(a || 0) - Number(b || 0)

export const useCounselorsSection = ({
	counselors,
	students,
	accessOptions,
	onUpdateAccess,
	onRemoveCounselor,
}) => {
	const [search, setSearch] = useState('')
	const [sortKey, setSortKey] = useState(null)
	const [sortDir, setSortDir] = useState('asc')
	const [sortLabel, setSortLabel] = useState('')
	const [savingAccessId, setSavingAccessId] = useState(null)
	const [removingId, setRemovingId] = useState(null)
	const [deleteConfirm, setDeleteConfirm] = useState(null)

	const rows = useMemo(
		() =>
			counselors.map(item => ({
				...item,
				assignedCount:
					item.caseload != null
						? Number(item.caseload) || 0
						: students.filter(row => row.counselor === item.name).length,
			})),
		[counselors, students],
	)

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase()
		const next = rows.filter(row => !query || row.name.toLowerCase().includes(query))

		if (!sortKey) return next

		const sorted = [...next].sort((a, b) => {
			let result = 0
			if (sortKey === 'counselor') result = compareText(a.name, b.name)
			if (sortKey === 'students') result = compareNumber(a.assignedCount, b.assignedCount)
			if (sortKey === 'access') result = compareText(a.access, b.access)
			return sortDir === 'desc' ? -result : result
		})

		return sorted
	}, [rows, search, sortDir, sortKey])

	const filterChips = useMemo(() => {
		if (!sortKey || !sortLabel) return []
		return [{ id: 'sort', label: sortLabel, kind: 'sort' }]
	}, [sortKey, sortLabel])

	const setSort = useCallback((key, dir, label) => {
		setSortKey(key)
		setSortDir(dir)
		setSortLabel(label)
	}, [])

	const clearSort = useCallback(() => {
		setSortKey(null)
		setSortDir('asc')
		setSortLabel('')
	}, [])

	const removeFilterChip = useCallback(
		chip => {
			if (chip?.kind === 'sort' || chip?.id === 'sort') clearSort()
		},
		[clearSort],
	)

	const clearAllPanelFilters = useCallback(() => {
		clearSort()
	}, [clearSort])

	const setAccess = useCallback(
		async (id, access) => {
			const row = counselors.find(item => item.id === id)
			if (!row || row.access === access || savingAccessId) return

			setSavingAccessId(id)
			try {
				await onUpdateAccess?.({ counselorId: id, accessLabel: access })
			} finally {
				setSavingAccessId(null)
			}
		},
		[counselors, onUpdateAccess, savingAccessId],
	)

	const requestRemoveCounselor = useCallback(row => {
		setDeleteConfirm(row)
	}, [])

	const cancelDeleteConfirm = useCallback(() => {
		if (removingId) return
		setDeleteConfirm(null)
	}, [removingId])

	const confirmDelete = useCallback(async () => {
		if (!deleteConfirm?.id || removingId) return

		setRemovingId(deleteConfirm.id)
		try {
			await onRemoveCounselor?.({ counselorId: deleteConfirm.id })
			setDeleteConfirm(null)
		} finally {
			setRemovingId(null)
		}
	}, [deleteConfirm, onRemoveCounselor, removingId])

	return {
		filtered,
		search,
		setSearch,
		sortKey,
		sortDir,
		filterChips,
		setSort,
		removeFilterChip,
		clearAllPanelFilters,
		setAccess,
		accessOptions,
		savingAccessId,
		removingId,
		deleteConfirm,
		requestRemoveCounselor,
		cancelDeleteConfirm,
		confirmDelete,
	}
}
